import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { getSiteUrl } from './siteConfig';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
// Get site URL from our utility function
const siteUrl = getSiteUrl();

// Log environment variables in development
if (import.meta.env.DEV) {
  console.log('Supabase URL:', supabaseUrl ? 'Set' : 'Not set');
  console.log('Supabase Anon Key:', supabaseAnonKey ? 'Set' : 'Not set');
  console.log('Site URL:', siteUrl || 'Not set (will use runtime origin)');
}

// Only throw error in development
if (import.meta.env.DEV && (!supabaseUrl || !supabaseAnonKey)) {
  throw new Error('Missing Supabase environment variables. Please check your .env file and Vercel environment variables.');
}

const STORAGE_KEY = 'sb-' + supabaseUrl?.split('//')[1]?.split('.')[0] + '-auth-token';

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 5000; // 5 seconds
const RETRY_BACKOFF_FACTOR = 2;

// Enhanced storage for PWA with IndexedDB fallback
const createPersistentStorage = () => {
  return {
    getItem: async (key: string): Promise<string | null> => {
      try {
        // Try localStorage first
        const value = localStorage.getItem(key);
        if (value) return value;
        
        // Fallback to IndexedDB for PWAs that might clear localStorage
        if ('indexedDB' in window) {
          const db = await openDB();
          const transaction = db.transaction(['auth'], 'readonly');
          const store = transaction.objectStore('auth');
          const request = store.get(key);
          
          return new Promise<string | null>((resolve) => {
            request.onsuccess = () => resolve(request.result?.value || null);
            request.onerror = () => resolve(null); // Don't reject, just return null
          });
        }
        
        return null;
      } catch (error) {
        console.warn('Error getting item from storage:', error);
        return null;
      }
    },
    
    setItem: async (key: string, value: string) => {
      try {
        // Set in localStorage
        localStorage.setItem(key, value);
        
        // Also set in IndexedDB as backup for PWAs
        if ('indexedDB' in window) {
          const db = await openDB();
          const transaction = db.transaction(['auth'], 'readwrite');
          const store = transaction.objectStore('auth');
          store.put({ key, value });
        }
      } catch (error) {
        console.warn('Error setting item in storage:', error);
        // Try IndexedDB if localStorage fails (quota exceeded)
        if ('indexedDB' in window) {
          try {
            const db = await openDB();
            const transaction = db.transaction(['auth'], 'readwrite');
            const store = transaction.objectStore('auth');
            store.put({ key, value });
          } catch (idbError) {
            console.error('Error setting item in IndexedDB:', idbError);
          }
        }
      }
    },
    
    removeItem: async (key: string) => {
      try {
        localStorage.removeItem(key);
        
        if ('indexedDB' in window) {
          const db = await openDB();
          const transaction = db.transaction(['auth'], 'readwrite');
          const store = transaction.objectStore('auth');
          store.delete(key);
        }
      } catch (error) {
        console.warn('Error removing item from storage:', error);
      }
    }
  };
};

// IndexedDB helper for PWA storage
const openDB = () => {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open('supabase-auth', 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('auth')) {
        db.createObjectStore('auth', { keyPath: 'key' });
      }
    };
  });
};

// Create the Supabase client with error handling
let supabase: SupabaseClient | null = null;
try {
  if (supabaseUrl && supabaseAnonKey) {
    supabase = createClient(supabaseUrl, supabaseAnonKey, { 
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        // Enable PKCE flow for better security with redirects
        flowType: 'pkce',
        detectSessionInUrl: true,
        // Use enhanced storage for better PWA persistence
        storage: createPersistentStorage()
      },
      global: {
        headers: { 
          'x-application-name': 'booking-management-system',
          'Cache-Control': 'no-cache' // Prevent auth caching
        },
        fetch: async (url, options = {}) => {
          let attempt = 0;
          let delay = INITIAL_RETRY_DELAY;

          while (attempt < MAX_RETRIES) {
            try {
              const response = await fetch(url, {
                ...options,
                // Ensure auth requests bypass service worker cache
                cache: 'no-cache'
              });
              
              // Handle auth errors
              if (response.status === 401) {
                // Clear auth data and redirect to login
                localStorage.removeItem(STORAGE_KEY);
                // Also clear IndexedDB
                if ('indexedDB' in window) {
                  try {
                    const db = await openDB();
                    const transaction = db.transaction(['auth'], 'readwrite');
                    const store = transaction.objectStore('auth');
                    store.clear();
                  } catch (err) {
                    console.warn('Failed to clear IndexedDB auth data:', err);
                  }
                }
                window.location.href = '/login';
                throw new Error('Authentication expired');
              }

              // Only retry on network errors or 5xx server errors 
              if (response.ok || (response.status < 500 && response.status !== 401)) {
                return response;
              }
              
              throw new Error(`HTTP error! status: ${response.status}`);
            } catch (err) {
              attempt++;
              console.warn(`Supabase fetch attempt ${attempt} failed:`, err);
              
              if (attempt === MAX_RETRIES) {
                throw new Error(`Request failed after ${MAX_RETRIES} retries: ${err instanceof Error ? err.message : 'Unknown error'}`);
              }
              
              // Exponential backoff with jitter
              const jitter = Math.random() * 200;
              delay = Math.min(delay * RETRY_BACKOFF_FACTOR + jitter, MAX_RETRY_DELAY);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          }
          throw new Error('Network request failed');
        }
      },
      db: {
        schema: 'public'
      }
    });
    console.log('Supabase client initialized successfully with enhanced PWA persistence');
  } else {
    console.error('Failed to initialize Supabase client: Missing environment variables');
  }
} catch (error) {
  console.error('Error initializing Supabase client:', error);
}

export { supabase };